import { useCallback, useEffect, useState } from "react";
import firebase from "../../configs/FirebaseConfig";
import { useSelector } from "react-redux";

const db = firebase.firestore();

/**
 * Hook to fetch and manage CRM notes from Firestore
 * @param {Object} options - Configuration options
 * @param {string} options.userId - User ID to fetch notes for (optional)
 * @param {boolean} options.autoFetch - Whether to automatically fetch data on mount (default: true)
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {boolean} options.fetchLatestPerUser - Fetch only the latest note per user (default: false)
 * @returns {Object} - Notes data and utility functions
 */
export const useCrmNotes = (options = {}) => {
  const { userId = null, autoFetch = true, realtime = true, fetchLatestPerUser = false } = options;
  const auth = useSelector((state) => state.firebaseReducer.auth);

  const [notes, setNotes] = useState([]);
  const [latestNotesMap, setLatestNotesMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch CRM notes for a specific user
   * @param {string} userId - User ID to fetch notes for
   * @returns {Promise<Array>} - Array of note documents
   */
  const fetchNotes = useCallback(async (userId) => {
    if (!userId) {
      console.warn("useCrmNotes.fetchNotes: No userId provided");
      return [];
    }

    setIsLoading(true);
    setError(null);

    try {
      const snapshot = await db
        .collection("users")
        .doc(userId)
        .collection("crmNotes")
        .orderBy("dateTime", "desc")
        .get();

      if (snapshot.empty) {
        setNotes([]);
        return [];
      }

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setNotes(data);
      return data;
    } catch (err) {
      console.error("Error fetching CRM notes:", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch the latest note for each user
   * This is optimized for displaying "last contact" information
   * @param {Array} userIds - Array of user IDs to fetch latest notes for (optional)
   * @returns {Promise<Object>} - Map of userId -> latest note timestamp
   */
  const fetchLatestNotesForUsers = useCallback(async (userIds = []) => {
    setIsLoading(true);
    setError(null);

    try {
      const latestMap = {};

      if (userIds.length === 0) {
        console.warn("useCrmNotes.fetchLatestNotesForUsers: No userIds provided");
        return {};
      }

      {
        // Fetch latest note for specific users (more efficient for small sets)
        const promises = userIds.map(async (userId) => {
          try {
            const snapshot = await db
              .collection("users")
              .doc(userId)
              .collection("crmNotes")
              .orderBy("dateTime", "desc")
              .limit(1)
              .get();

            if (!snapshot.empty) {
              const latestNote = snapshot.docs[0].data();
              const timestamp = latestNote.dateTime || latestNote.createdAt;
              return { userId, timestamp };
            }
            return null;
          } catch (err) {
            console.error(`Error fetching latest note for user ${userId}:`, err);
            return null;
          }
        });

        const results = await Promise.all(promises);
        results.forEach((result) => {
          if (result) {
            latestMap[result.userId] = result.timestamp;
          }
        });
      }

      setLatestNotesMap(latestMap);
      return latestMap;
    } catch (err) {
      console.error("Error fetching latest notes for users:", err);
      setError(err);
      return {};
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get notes sorted by dateTime (or createdAt as fallback)
   * @returns {Array} - Sorted notes
   */
  const getSortedNotes = useCallback(() => {
    return [...notes].sort((a, b) => {
      const aTime = a.dateTime || a.createdAt || 0;
      const bTime = b.dateTime || b.createdAt || 0;
      return bTime - aTime;
    });
  }, [notes]);

  /**
   * Get notes with attachments
   * @returns {Array} - Filtered notes with attachments
   */
  const getNotesWithAttachments = useCallback(() => {
    return notes.filter((note) => note?.attachments && note.attachments.length > 0);
  }, [notes]);

  // Set up real-time listener or fetch once
  useEffect(() => {
    if (!autoFetch) return;

    if (auth.isEmpty) {
      setNotes([]);
      setLatestNotesMap({});
      return;
    }

    // If fetchLatestPerUser is enabled, don't set up regular listener
    if (fetchLatestPerUser) {
      return;
    }

    // Don't query if no userId is provided
    if (!userId) {
      console.warn("useCrmNotes: No userId provided, skipping query");
      setNotes([]);
      return;
    }

    if (realtime) {
      // Real-time listener for specific user's notes
      setIsLoading(true);
      const unsubscribe = db
        .collection("users")
        .doc(userId)
        .collection("crmNotes")
        .orderBy("dateTime", "desc")
        .onSnapshot(
          (snapshot) => {
            const data = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setNotes(data);
            setIsLoading(false);
            setError(null);
          },
          (err) => {
            console.error("useCrmNotes: Error in listener:", err);
            setError(err);
            setNotes([]);
            setIsLoading(false);
          },
        );

      return () => {
        if (unsubscribe) unsubscribe();
      };
    } else {
      // One-time fetch
      fetchNotes(userId);
    }
  }, [autoFetch, userId, realtime, fetchLatestPerUser, fetchNotes, auth.isEmpty]);

  return {
    notes,
    latestNotesMap,
    isLoading,
    error,
    fetchNotes,
    fetchLatestNotesForUsers,
    getSortedNotes,
    getNotesWithAttachments,
  };
};
